/**
 * Recover images that failed because the session lapsed underneath them.
 *
 * 🔴 THE BUG THIS EXISTS FOR — and why the proactive refresh in `api.js` is
 * not enough on its own.
 *
 * `/uploads/**` requires a session, and `serve_uploads` answers **404** on any
 * auth failure (deliberately — a 403 would confirm the file exists). The SPA
 * copes with an expired access token: it gets a 401, refreshes, retries. A
 * plain `<img src="/uploads/…">` cannot. It carries only the cookie, and an
 * `<img>` HAS NO RETRY. So the moment the cookie lapsed, every avatar, banner
 * and thumbnail on the page went blank and STAYED blank, while the page around
 * them kept working perfectly — until somebody hard-reloaded.
 *
 * Measured on the intranet: valid cookie → 200, expired cookie → 404, first
 * occurrence 2026-08-07. It read as "the images have vanished", which is
 * nowhere near where the fault actually was.
 *
 * The scheduler shrinks that window; it cannot close it. A laptop sleeps
 * through its own timer, a tab comes back from bfcache, the clock skews, the
 * network drops mid-refresh — and the very first paint after waking is
 * precisely when every image on the page requests at once. So: prevention in
 * `api.js`, cure here.
 *
 * Installed once from the barrel, like the Esc-dismiss and dirty guards.
 */
import { api } from './api.js';

/* Prefixes that need a session. Anything else — a CDN logo, a data: URI, a
   cross-origin avatar — is somebody else's problem and must not trigger a
   refresh. */
const PROTECTED_PREFIXES = ['/uploads/', '/api/'];

/* ⚠️ Hard ceilings. A genuinely deleted file 404s for ever, and without a
   bound one missing image on a busy page would retry, fail, and ask for a
   refresh again on every render. */
const MAX_TOTAL_RETRIES = 40;
const RECOVERY_COOLDOWN_MS = 60 * 1000;

const RETRY_PARAM = '_isgr';

let installed = false;
let retriesUsed = 0;
let recoveryInFlight = null;
let lastRecoveryAt = 0;

function isProtectedUrl(raw) {
  if (!raw) return false;
  try {
    const u = new URL(raw, window.location.href);
    // ⚠️ Same-origin ONLY. A refresh on this origin cannot fix a foreign
    // host's 404, and asking for one would be a request per broken remote
    // image.
    if (u.origin !== window.location.origin) return false;
    return PROTECTED_PREFIXES.some(p => u.pathname.startsWith(p));
  } catch (_) {
    return false;
  }
}

/* One refresh serves every image that failed in the same wave — a page can
   easily hold thirty of them, and thirty refreshes would be a stampede
   against the one endpoint whose failure signs people out. */
function recoverSession() {
  const now = Date.now();
  if (recoveryInFlight) return recoveryInFlight;
  if (now - lastRecoveryAt < RECOVERY_COOLDOWN_MS) return Promise.resolve(false);
  lastRecoveryAt = now;
  recoveryInFlight = Promise.resolve(api.recoverSession())
    .catch(() => false)
    .finally(() => { recoveryInFlight = null; });
  return recoveryInFlight;
}

function retryUrl(raw) {
  try {
    const u = new URL(raw, window.location.href);
    // ⚠️ A distinct URL, not the same string reassigned. Setting `src` to the
    // value it already holds does not reliably re-fetch, and a 404 sitting in
    // the HTTP cache would be served straight back.
    u.searchParams.set(RETRY_PARAM, '1');
    return u.pathname + u.search + u.hash;
  } catch (_) {
    return raw;
  }
}

async function onResourceError(event) {
  const el = event?.target;
  if (!el || el.tagName !== 'IMG') return;
  // Already given its one chance — a second failure is a real missing file.
  if (el.dataset.isgImgRetried) return;
  if (retriesUsed >= MAX_TOTAL_RETRIES) return;

  const src = el.currentSrc || el.getAttribute('src') || '';
  if (!isProtectedUrl(src) || src.includes(`${RETRY_PARAM}=`)) return;

  el.dataset.isgImgRetried = '1';
  retriesUsed += 1;

  const ok = await recoverSession();
  // 🔴 Only retry when a refresh actually SUCCEEDED. Retrying regardless
  // would double the request count for every genuinely broken image and
  // teach nobody anything.
  if (!ok) return;
  el.src = retryUrl(src);
}

/**
 * Install the listener and start the session keep-alive.
 *
 * ⚠️ CAPTURE PHASE. A resource `error` from an `<img>` does not bubble, so a
 * listener on `document` only ever sees it during capture. Bound once at the
 * document, so images added by any later render are covered without every
 * page having to remember to wire something.
 */
export function initImageRecovery() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('error', onResourceError, true);
  try {
    api.startSessionKeepAlive();
  } catch (_) { /* never let this break an app's boot */ }
}

/* Test seam — lets a suite assert the bounds without waiting a minute. */
export function _resetImageRecovery() {
  retriesUsed = 0;
  recoveryInFlight = null;
  lastRecoveryAt = 0;
}
